import { ZodType } from 'zod/v3';
import { fold, ZodAlgebra } from './fold';

// Helper function to extract description from schema
const getDescription = (schema: any): string | undefined => {
  return schema._def?.description;
};

// Helper function to format type with optional description comment
const formatWithDescription = (type: string, description?: string): string => {
  return description ? `${type} // ${description}` : type;
};

// TypeScript formatting algebra for direct Zod schemas
const prettyPrintAlgebra = (indent = 0): ZodAlgebra<string> => {
  const spaces = '  '.repeat(indent);

  return {
    string: (schema) => formatWithDescription('string', getDescription(schema)),
    number: (schema) => formatWithDescription('number', getDescription(schema)),
    boolean: (schema) =>
      formatWithDescription('boolean', getDescription(schema)),
    any: (schema) => formatWithDescription('any', getDescription(schema)),
    unknown: (schema) =>
      formatWithDescription('unknown', getDescription(schema)),
    void: (schema) => formatWithDescription('void', getDescription(schema)),
    null: (schema) => formatWithDescription('null', getDescription(schema)),
    undefined: (schema) =>
      formatWithDescription('undefined', getDescription(schema)),
    never: (schema) => formatWithDescription('never', getDescription(schema)),
    bigint: (schema) => formatWithDescription('bigint', getDescription(schema)),
    symbol: (schema) => formatWithDescription('symbol', getDescription(schema)),
    date: (schema) => formatWithDescription('Date', getDescription(schema)),
    literal: (schema) => {
      const value = (schema as any)._def.value;
      const literalType =
        typeof value === 'string' ? `"${value}"` : String(value);
      return formatWithDescription(literalType, getDescription(schema));
    },
    array: (schema, element) =>
      formatWithDescription(`${element}[]`, getDescription(schema)),
    object: (_schema, properties) => {
      const entries = Object.entries(properties);
      if (entries.length === 0) return '{}';

      const lines = ['{'];
      for (const [key, value] of entries) {
        const optional = (value as any)?.optional ? '?' : '';
        const formattedValue =
          typeof value === 'string' ? value : String(value);

        // Handle multi-line nested objects with proper indentation
        if (formattedValue.includes('\n')) {
          const indentedValue = formattedValue
            .split('\n')
            .map((line, i) => (i === 0 ? line : `${spaces}  ${line}`))
            .join('\n');
          lines.push(`${spaces}  ${key}${optional}: ${indentedValue}`);
        } else {
          lines.push(`${spaces}  ${key}${optional}: ${formattedValue}`);
        }
      }
      lines.push(`${spaces}}`);
      return lines.join('\n');
    },
    union: (_schema, types) => {
      if (types.length === 0) return 'never';
      if (types.length === 1) return types[0]!;

      // Check if it's a simple union that can go on one line
      const simpleTypes = ['string', 'number', 'boolean', 'null', 'undefined'];
      const isSimple = types.every(
        (t) =>
          typeof t === 'string' &&
          (simpleTypes.includes(t) || t.startsWith('"'))
      );

      if (isSimple) {
        return types.join(' | ');
      }

      // Multi-line union
      return types
        .map((t, i) => {
          const prefix = i === 0 ? '' : `${spaces}| `;
          return prefix + t;
        })
        .join('\n');
    },
    intersection: (_schema, left, right) => `${left} & ${right}`,
    tuple: (_schema, elements) => `[${elements.join(', ')}]`,
    record: (_schema, key, value) => `Record<${key}, ${value}>`,
    map: (_schema, key, value) => `Map<${key}, ${value}>`,
    set: (_schema, element) => `Set<${element}>`,
    function: (_schema, args, returns) => {
      const argStrings = args.map((arg, i) => `arg${i}: ${arg}`);
      return `(${argStrings.join(', ')}) => ${returns}`;
    },
    lazy: (schema) => {
      const description = (schema as any)._def.description;
      return description || 'any /* recursive */';
    },
    enum: (schema) => {
      const values = (schema as any)._def.values;
      return values.map((v: string) => `"${v}"`).join(' | ');
    },
    nativeEnum: (schema) => {
      const values = (schema as any)._def.values;
      const entries = Object.entries(values);

      // Check if this is a numeric enum
      const hasNumericValues = entries.some(
        ([_, value]) => typeof value === 'number'
      );

      if (hasNumericValues) {
        const numericValues = entries
          .filter(([_, value]) => typeof value === 'number')
          .map(([_, value]) => String(value));
        return numericValues.join(' | ');
      } else {
        const stringValues = entries
          .filter(([_, value]) => typeof value === 'string')
          .map(([_, value]) => `"${value}"`);
        return stringValues.join(' | ');
      }
    },
    promise: (_schema, element) => `Promise<${element}>`,
    optional: (_schema, inner) => {
      // For optional, we usually don't add | undefined since it's handled by the ? in object properties
      // But for standalone optionals, we do need the union
      return `${inner} | undefined`;
    },
    nullable: (_schema, inner) => {
      // Flatten unions when adding null
      if (typeof inner === 'string' && inner.includes(' | ')) {
        return `${inner} | null`;
      }
      return `${inner} | null`;
    },
    default: (_schema, inner) => inner, // Default doesn't affect the type
    effects: (_schema, inner) => inner, // Effects don't affect the underlying type
  };
};

/**
 * Pretty print a Zod schema as a TypeScript type string
 */
export const prettyPrint = (schema: ZodType): string => {
  return fold(prettyPrintAlgebra(), schema);
};
